import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Check, Sun, Moon } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { isotaneDoseForDay, entryCompletion } from "@/lib/skincare";
import { api } from "@/lib/api";

function ChecklistRow({ item, checked, onToggle, special }) {
  return (
    <motion.button
      onClick={onToggle}
      whileTap={{ scale: 0.97 }}
      className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-colors text-left ${
        checked
          ? "bg-[#E9F0E8] border-[#CDE0D1]"
          : "bg-white border-[#E9EFEA]"
      }`}
    >
      <motion.div
        animate={checked ? { scale: [1, 1.2, 1] } : { scale: 1 }}
        transition={{ duration: 0.3 }}
        className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2 ${
          checked
            ? "bg-[#4A6B53] border-[#4A6B53]"
            : "bg-white border-[#CDE0D1]"
        }`}
      >
        {checked && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
      </motion.div>
      <div className="flex-1 min-w-0">
        <div
          className={`font-heading font-medium ${
            checked ? "text-[#6B7A70] line-through" : "text-[#2A332C]"
          }`}
        >
          {item.label}
          {special && (
            <span className="ml-2 text-xs px-2 py-0.5 bg-[#CC7A6B]/15 text-[#CC7A6B] rounded-full">
              {special}
            </span>
          )}
        </div>
        <div className="text-xs text-[#6B7A70] mt-0.5">{item.desc}</div>
      </div>
    </motion.button>
  );
}

export default function PastDaySheet({
  open,
  onClose,
  date,
  dayNum,
  initialEntry,
  morningItems,
  nightItems,
  onSaved,
}) {
  const [entry, setEntry] = useState(
    initialEntry || { morning: {}, night: {}, symptoms: [], notes: "", mood: null, water: 0 }
  );
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const dose = isotaneDoseForDay(dayNum);

  // Reset local state whenever a new day is opened
  useEffect(() => {
    if (open) {
      setEntry(
        initialEntry || { morning: {}, night: {}, symptoms: [], notes: "", mood: null, water: 0 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date]);

  const toggle = (section, id) => {
    const next = {
      ...entry,
      [section]: { ...entry[section], [id]: !entry[section]?.[id] },
    };
    setEntry(next);

    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving(true);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.saveEntry(date, next);
        onSaved?.(date, next);
      } catch {}
      setSaving(false);
    }, 400);
  };

  const dateLabel = new Date(date + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  const completion = entryCompletion(entry, morningItems, nightItems);
  const completionPct = Math.round(completion * 100);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl max-h-[88vh] overflow-y-auto bg-[#F7F5F0] px-0 pb-10"
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-2 pb-4 border-b border-[#E9EFEA]">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7A70] mb-0.5">
                Day {dayNum}
              </div>
              <SheetTitle className="font-heading text-xl text-[#2A332C] text-left">
                {dateLabel}
              </SheetTitle>
            </div>
            <div className="flex items-center gap-3">
              {saving ? (
                <span className="text-xs text-[#6B7A70]">Saving…</span>
              ) : (
                <span
                  className={`text-xs font-semibold ${
                    completion === 1 ? "text-[#4A6B53]" : "text-[#6B7A70]"
                  }`}
                >
                  {completionPct}%
                </span>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Morning section */}
        <div className="px-6 mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Sun className="w-4 h-4 text-[#E6B97A]" strokeWidth={1.8} />
            <h3 className="font-heading text-base font-medium text-[#2A332C]">Morning</h3>
          </div>
          <div className="space-y-2">
            {morningItems.map((item) => (
              <ChecklistRow
                key={item.id}
                item={item}
                checked={!!entry?.morning?.[item.id]}
                onToggle={() => toggle("morning", item.id)}
              />
            ))}
          </div>
        </div>

        {/* Night section */}
        <div className="px-6 mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Moon className="w-4 h-4 text-[#4A6B53]" strokeWidth={1.8} />
            <h3 className="font-heading text-base font-medium text-[#2A332C]">Night</h3>
          </div>
          <div className="space-y-2">
            {nightItems.map((item) => (
              <ChecklistRow
                key={item.id}
                item={item}
                checked={!!entry?.night?.[item.id]}
                onToggle={() => toggle("night", item.id)}
                special={item.id === "isotane" ? dose : null}
              />
            ))}
          </div>
        </div>

        {completion === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-6 mt-5 p-3 rounded-2xl bg-[#E9F0E8] border border-[#CDE0D1] text-center"
          >
            <div className="text-sm font-heading font-medium text-[#4A6B53]">
              ✓ Day {dayNum} fully logged
            </div>
          </motion.div>
        )}
      </SheetContent>
    </Sheet>
  );
}
